import {
  EntitiesResponseSchema,
  EntityDetailResponseSchema,
  EventDetailResponseSchema,
  EventsResponseSchema,
  ExplainResponseSchema,
  GraphResponseSchema,
  IndexResponseSchema,
  RelationshipsResponseSchema,
  SimulateResponseSchema,
  TimelineResponseSchema,
  type ActiveLayer,
  type EntitiesResponse,
  type EventsResponse,
  type ExplainResponse,
  type GraphResponse,
  type IndexResponse,
  type RelationshipsResponse,
  type SimulateResponse,
  type TimelineResponse,
  type Weights,
} from "@nexus/contracts";

const API_BASE = "";

export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(
  path: string,
  options?: RequestInit,
): Promise<T> {
  const url = `${API_BASE}${path}`;
  const response = await fetch(url, {
    ...options,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(options?.headers || {}),
    },
  });

  if (!response.ok) {
    let errorData = { error: { code: "internal", message: response.statusText } };
    try {
      errorData = await response.json();
    } catch {
      // ignore
    }
    throw new ApiError(
      errorData.error?.code || "internal",
      errorData.error?.message || `HTTP ${response.status}`,
      response.status,
    );
  }

  return (await response.json()) as T;
}

export async function fetchHealth(): Promise<{ ok: boolean; dataset: string; version: string }> {
  return request<{ ok: boolean; dataset: string; version: string }>("/api/health");
}

export async function fetchEntities(params?: {
  type?: string;
  indexEligible?: boolean;
  limit?: number;
  offset?: number;
}): Promise<EntitiesResponse> {
  const sp = new URLSearchParams();
  if (params?.type) sp.set("type", params.type);
  if (params?.indexEligible !== undefined) sp.set("indexEligible", String(params.indexEligible));
  if (params?.limit) sp.set("limit", String(params.limit));
  if (params?.offset) sp.set("offset", String(params.offset));

  const qs = sp.toString() ? `?${sp.toString()}` : "";
  const raw = await request<unknown>(`/api/entities${qs}`);
  return EntitiesResponseSchema.parse(raw);
}

export async function fetchEntityDetail(id: string) {
  const raw = await request<unknown>(`/api/entities/${encodeURIComponent(id)}`);
  return EntityDetailResponseSchema.parse(raw);
}

export async function fetchEvents(params?: {
  layer?: ActiveLayer;
  eventType?: string;
  actorId?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}): Promise<EventsResponse> {
  const sp = new URLSearchParams();
  if (params?.layer) sp.set("layer", params.layer);
  if (params?.eventType) sp.set("eventType", params.eventType);
  if (params?.actorId) sp.set("actorId", params.actorId);
  if (params?.from) sp.set("from", params.from);
  if (params?.to) sp.set("to", params.to);
  if (params?.limit) sp.set("limit", String(params.limit));
  if (params?.offset) sp.set("offset", String(params.offset));

  const qs = sp.toString() ? `?${sp.toString()}` : "";
  const raw = await request<unknown>(`/api/events${qs}`);
  return EventsResponseSchema.parse(raw);
}

export async function fetchEventDetail(id: string) {
  const raw = await request<unknown>(`/api/events/${encodeURIComponent(id)}`);
  return EventDetailResponseSchema.parse(raw);
}

export async function fetchRelationships(params?: {
  asOf?: string;
  relType?: string;
  entityId?: string;
  technologyId?: string;
  limit?: number;
  offset?: number;
}): Promise<RelationshipsResponse> {
  const sp = new URLSearchParams();
  if (params?.asOf) sp.set("asOf", params.asOf);
  if (params?.relType) sp.set("relType", params.relType);
  if (params?.entityId) sp.set("entityId", params.entityId);
  if (params?.technologyId) sp.set("technologyId", params.technologyId);
  if (params?.limit) sp.set("limit", String(params.limit));
  if (params?.offset) sp.set("offset", String(params.offset));

  const qs = sp.toString() ? `?${sp.toString()}` : "";
  const raw = await request<unknown>(`/api/relationships${qs}`);
  return RelationshipsResponseSchema.parse(raw);
}

export async function fetchIndexRanking(params?: {
  weightSetId?: string;
  period?: string;
}): Promise<IndexResponse> {
  const sp = new URLSearchParams();
  if (params?.weightSetId) sp.set("weightSetId", params.weightSetId);
  if (params?.period) sp.set("period", params.period);

  const qs = sp.toString() ? `?${sp.toString()}` : "";
  const raw = await request<unknown>(`/api/index${qs}`);
  return IndexResponseSchema.parse(raw);
}

export async function fetchExplain(
  entityId: string,
  params?: { weightSetId?: string; period?: string },
): Promise<ExplainResponse> {
  const sp = new URLSearchParams();
  if (params?.weightSetId) sp.set("weightSetId", params.weightSetId);
  if (params?.period) sp.set("period", params.period);

  const qs = sp.toString() ? `?${sp.toString()}` : "";
  const raw = await request<unknown>(`/api/index/${encodeURIComponent(entityId)}/explain${qs}`);
  return ExplainResponseSchema.parse(raw);
}

export async function simulateWeights(
  weights: Weights,
  period?: string,
): Promise<SimulateResponse> {
  const raw = await request<unknown>("/api/index/simulate", {
    method: "POST",
    body: JSON.stringify({ weights, period }),
  });
  return SimulateResponseSchema.parse(raw);
}

export async function fetchGraph(params?: {
  asOf?: string;
  layer?: ActiveLayer;
}): Promise<GraphResponse> {
  const sp = new URLSearchParams();
  if (params?.asOf) sp.set("asOf", params.asOf);
  if (params?.layer) sp.set("layer", params.layer);

  const qs = sp.toString() ? `?${sp.toString()}` : "";
  const raw = await request<unknown>(`/api/graph${qs}`);
  return GraphResponseSchema.parse(raw);
}

export async function fetchTimeline(params?: {
  layer?: ActiveLayer;
  from?: string;
  to?: string;
}): Promise<TimelineResponse> {
  const sp = new URLSearchParams();
  if (params?.layer) sp.set("layer", params.layer);
  if (params?.from) sp.set("from", params.from);
  if (params?.to) sp.set("to", params.to);

  const qs = sp.toString() ? `?${sp.toString()}` : "";
  const raw = await request<unknown>(`/api/timeline${qs}`);
  return TimelineResponseSchema.parse(raw);
}
